import React, { useState, useCallback, useContext, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Image from 'next/image';
import Button from '../Button';
import { useTranslation } from 'next-i18next';
import NavigationSearch from '../NavigationSearch';
import NavigationFavorite from '../NavigationFavorite/';
import { kebabToPascal } from '../../utils/caseconverters';
import BaseContext from '../../containers/BasePage/BaseContext';
import { CookieStateContext } from '../../containers/BasePage/state';
import logoVisitSweden from '../../public/img/logotype-visitsweden.svg';
import styles from './NavigationMobile.module.scss';

const NavigationMobile = ({
    menu = [],
    searchPageUrl = '',
    favoritesPageUrl = '',
    languages = [],
    handleHeadroom = () => false,
    showMinimalHeader = false,
    heroTextTheme = 'default',
}) => {
    const { t } = useTranslation('common');
    const { isStorybook } = useContext(BaseContext);

    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [activeSubmenu, setActiveSubmenu] = useState(null);
    const [submenuContent, setSubmenuContent] = useState(null);

    useEffect(() => {
        if (menuIsOpen) {
            document.body.classList.add('navigation-mobile--open');
        } else {
            document.body.classList.remove('navigation-mobile--open');
        }
    }, [menuIsOpen]);

    const handleMenuToggle = useCallback(() => {
        handleHeadroom();
        setMenuIsOpen((menuIsOpen) => !menuIsOpen);
        setShowSearch(false);
        // Reset submenu when closing main menu
        if (menuIsOpen) {
            setActiveSubmenu(null);
            setTimeout(() => setSubmenuContent(null), 300);
        }
    }, [handleHeadroom, menuIsOpen]);

    const handleSubmenuOpen = useCallback((menuItem) => {
        setActiveSubmenu(menuItem);
        setSubmenuContent(menuItem);
    }, []);

    const handleSubmenuClose = useCallback(() => {
        setActiveSubmenu(null);
        setTimeout(() => setSubmenuContent(null), 300);
    }, []);

    const { state } = useContext(CookieStateContext) || {};
    const cookieConsent = state?.categories?.includes('functionality');

    const textThemeModifier = heroTextTheme ? kebabToPascal(heroTextTheme) : '';

    // Only pass light or dark theme to favorite when closed
    const favoriteTheme = menuIsOpen ? '' : heroTextTheme;
    const favoritesUrl = favoritesPageUrl && (cookieConsent || isStorybook) ? favoritesPageUrl : '';

    const menuClasses = classNames(
        styles['NavigationMobile'],
        { [styles['NavigationMobile--Minimal']]: showMinimalHeader },
        { [styles['NavigationMobile--ShowSearch']]: showSearch },
        { [styles['NavigationMobile--Open']]: menuIsOpen },
        { ['NavigationMobile--Open']: menuIsOpen }, // for headroom styling
        {
            [styles[`NavigationMobile--TextTheme${textThemeModifier}`]]: textThemeModifier,
        },
        { [styles['NavigationMobile--TextThemeDefault']]: !textThemeModifier }
    );

    return (
        <div className={menuClasses}>
            <div className={styles['NavigationMobile__Top']}>
                <a href="/" className={styles['NavigationMobile__Logotype']} aria-label={t('navigationMobile.logo')}>
                    <Image {...logoVisitSweden} overrideSrc={logoVisitSweden?.src} alt="VisitSweden" priority={true} />
                </a>
                <div className={styles['NavigationMobile__Actions']}>
                    <button
                        className={classNames(styles['NavigationMobile__Search'], {
                            [styles['NavigationMobile__Search--Active']]: showSearch,
                        })}
                        onClick={() => {
                            if (
                                typeof window !== 'undefined' &&
                                window.location.pathname !== '/' &&
                                searchPageUrl.indexOf(window.location.pathname) > -1 &&
                                document.getElementById('search-filter')
                            ) {
                                document.getElementById('search-filter').scrollIntoView({
                                    behavior: 'smooth',
                                });
                            } else {
                                setShowSearch(!showSearch);
                                setMenuIsOpen(false);
                            }
                        }}>
                        {t('navigation.search')}
                    </button>

                    <button
                        className={styles['NavigationMobile__Menu']}
                        onClick={handleMenuToggle}
                        aria-controls="navigation-mobile"
                        aria-expanded={menuIsOpen}>
                        {t('navigation.menu')}
                    </button>
                </div>
            </div>
            <NavigationSearch
                id="navigation-mobile"
                searchPageUrl={searchPageUrl}
                isVisible={showSearch}
                onClose={() => setShowSearch(false)}
                onToggle={() => setShowSearch(!showSearch)}
                modifier="NavigationMobile"
            />
            <div className={styles['NavigationMobile__Container']}>
                <div
                    className={classNames(styles['NavigationMobile__MainPanel'], {
                        [styles['NavigationMobile__MainPanel--Hidden']]: activeSubmenu,
                    })}>
                    <nav
                        className={classNames(styles['NavigationMobile__Items'], {
                            [styles['NavigationMobile__Items--Hidden']]: activeSubmenu,
                        })}
                        aria-label={t('navigationMobile.nav')}
                        id="navigation-mobile"
                        aria-hidden={!menuIsOpen}>
                        {menu.map((item, index) => (
                            <SubNavigation key={index} {...item} onSubmenuOpen={handleSubmenuOpen} />
                        ))}
                    </nav>
                    <Footer
                        favoritesPageUrl={favoritesUrl}
                        favoriteTheme={favoriteTheme}
                        languages={languages}
                        isMinimal={showMinimalHeader}
                        isShown={menuIsOpen && !submenuContent}
                    />
                </div>

                <div
                    className={classNames(styles['NavigationMobile__SubmenuPanel'], {
                        [styles['NavigationMobile__SubmenuPanel--Active']]: activeSubmenu,
                    })}>
                    {submenuContent && (
                        <>
                            <div className={styles['NavigationMobile__SubmenuContent']}>
                                <div className={styles['NavigationMobile__SubmenuHeader']}>
                                    <button
                                        className={styles['NavigationMobile__BackButton']}
                                        onClick={handleSubmenuClose}></button>
                                    <span className={styles['NavigationMobile__SubmenuTitle']}>
                                        {submenuContent.title}
                                    </span>
                                </div>
                                <div className={styles['NavigationMobile__SubmenuItems']}>
                                    {submenuContent.items?.flatMap(
                                        (lists, listsIndex) =>
                                            lists.items?.map(({ links, label: groupLabel, iconType }, listIndex) => {
                                                if (!links || links.length === 0) return null;

                                                return (
                                                    <React.Fragment key={`${listsIndex}-${listIndex}`}>
                                                        <div className={styles['NavigationMobile__GroupTitle']}>
                                                            {iconType && (
                                                                <i
                                                                    className={styles['NavigationMobile__GroupIcon']}
                                                                    style={{
                                                                        maskImage: `url('/img/wagtail-icons/${iconType}')`,
                                                                    }}
                                                                />
                                                            )}
                                                            {groupLabel}
                                                        </div>
                                                        {links.map((link, linkIndex) => (
                                                            <SubNavigationItem key={linkIndex} {...link} />
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            }) || []
                                    )}
                                    <div className={styles['NavigationMobile__ButtonContainer']}>
                                        <Button
                                            type="Primary"
                                            href={submenuContent.href}
                                            className={styles['NavigationMobile__CloseSubmenuButton']}>
                                            {submenuContent.title}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <Footer
                                favoritesPageUrl={favoritesUrl}
                                favoriteTheme={favoriteTheme}
                                languages={languages}
                                isMinimal={showMinimalHeader}
                                isShown={true}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

NavigationMobile.propTypes = {
    menu: PropTypes.array,
    searchPageUrl: PropTypes.string,
    favoritesPageUrl: PropTypes.string,
    languages: PropTypes.array,
    handleDisable: PropTypes.func,
    showMinimalHeader: PropTypes.bool,
    heroTextTheme: PropTypes.string,
};

const MobileLanguageSwitcher = ({ languages }) => {
    const { t } = useTranslation('common');
    const [subMenuIsOpen, setSubMenuIsOpen] = useState(false);
    const handleSubMenuToggle = useCallback(() => {
        setSubMenuIsOpen((subMenuIsOpen) => !subMenuIsOpen);
    }, []);
    const activeLanguage = languages.filter((l) => l.isActive)[0];

    const languageMenuClasses = classNames(styles['NavigationMobile__LanguageMenu'], {
        [styles['NavigationMobile__LanguageMenu--Open']]: subMenuIsOpen,
    });

    const languageSwitcherClasses = classNames(styles['NavigationMobile__LanguageSwitcher'], {
        [styles['NavigationMobile__LanguageSwitcher--Open']]: subMenuIsOpen,
    });

    if (!activeLanguage) {
        return null;
    }

    return (
        <div className={languageSwitcherClasses}>
            <button
                onClick={handleSubMenuToggle}
                aria-expanded={subMenuIsOpen}
                aria-controls="mobile-language-menu"
                className={styles['NavigationMobile__ActiveLanguage']}>
                {t(`navigation.language`)}
            </button>
            <div id="mobile-language-menu" aria-hidden={!subMenuIsOpen} className={languageMenuClasses}>
                {languages.map((language, idx) => {
                    const checkBoxClass = classNames(styles['NavigationMobile__Checkbox'], {
                        [styles['NavigationMobile__Checkbox--Active']]: language.isActive,
                    });

                    return (
                        <a
                            className={styles['NavigationMobile__LanguageLink']}
                            key={idx}
                            lang={language.langCode}
                            href={language.href}>
                            <span className={checkBoxClass} aria-hidden={true} />
                            {t(`navigation.languages.${language.langCode}`)}
                        </a>
                    );
                })}
            </div>
        </div>
    );
};

MobileLanguageSwitcher.propTypes = {
    languages: PropTypes.array,
};

const SubNavigation = ({ title, pageType, items, href, onSubmenuOpen }) => {
    const handleSubmenuClick = useCallback(
        (e) => {
            e.preventDefault();
            if (onSubmenuOpen) {
                onSubmenuOpen({ title, pageType, items, href });
            }
        },
        [onSubmenuOpen, title, pageType, items, href]
    );

    const topNavigationClasses = classNames(
        styles['NavigationMobile__Item'],
        styles['NavigationMobile__Item--TopLevel'],
        { [styles[`NavigationMobile__Item--${pageType}`]]: pageType }
    );

    const hasSubMenuLinkItems =
        items &&
        !!items.length &&
        items.some((firstLevelLists) => {
            return (
                firstLevelLists.items &&
                firstLevelLists.items.some((secondLevelLists) => {
                    return secondLevelLists.links && !!secondLevelLists.links.length && !!secondLevelLists.links.length;
                })
            );
        });

    return (
        <div className={styles['NavigationMobile__ItemContainer']}>
            {hasSubMenuLinkItems ? (
                <button className={topNavigationClasses} onClick={handleSubmenuClick}>
                    {title}
                </button>
            ) : (
                <a className={topNavigationClasses} href={href}>
                    {title}
                </a>
            )}
            {hasSubMenuLinkItems && (
                <div className={styles['NavigationMobile__ArrowButton']}>
                    <button className={styles['NavigationMobile__Arrow']} onClick={handleSubmenuClick}>
                        <span className={styles['NavigationMobile__Icon']}></span>
                    </button>
                </div>
            )}
        </div>
    );
};

SubNavigation.propTypes = {
    title: PropTypes.string,
    href: PropTypes.string,
    pageType: PropTypes.string,
    items: PropTypes.array,
    onSubmenuOpen: PropTypes.func,
};

const SubNavigationItem = ({ title, href }) => {
    const subNavigationClasses = classNames(
        styles['NavigationMobile__ItemContainer'],
        styles['NavigationMobile__ItemContainer--SubLevel']
    );

    const subNavigationLinkClasses = classNames(
        styles['NavigationMobile__Item'],
        styles['NavigationMobile__Item--SubLevel']
    );

    return (
        <div className={subNavigationClasses}>
            <span className={subNavigationLinkClasses}>{title}</span>
            <a href={href} className={styles['NavigationMobile__ItemLink']}>
                <span className="sr-only">{title}</span>
            </a>
        </div>
    );
};

SubNavigationItem.propTypes = {
    title: PropTypes.string,
    href: PropTypes.string,
};

const Footer = ({
    favoritesPageUrl = '',
    favoriteTheme = '',
    languages = null,
    isMinimal = false,
    isShown = false,
}) => {
    const { t } = useTranslation('common');

    return (
        <div className={styles['NavigationMobile__Footer']}>
            <div className={styles['NavigationMobile__FooterWrapper']}>
                <nav
                    className={styles['NavigationMobile__FooterNav']}
                    aria-label={t('navigationMobile.footerNav')}
                    id="navigation-mobile-footer"
                    aria-hidden={!isShown}>
                    {favoritesPageUrl && (
                        <NavigationFavorite
                            favoritesPageUrl={favoritesPageUrl}
                            theme={favoriteTheme}
                            isMinimal={isMinimal}
                        />
                    )}
                    <MobileLanguageSwitcher languages={languages} />
                </nav>
                <div className={styles['NavigationMobile__Byline']}>{t('navigation.byline')}</div>
            </div>
        </div>
    );
};

Footer.propTypes = {
    favoritesPageUrl: PropTypes.string,
    favoriteTheme: PropTypes.string,
    languages: PropTypes.array,
    isMinimal: PropTypes.bool,
    isShown: PropTypes.bool,
};

export default NavigationMobile;
